import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { BookOpen, User, LogOut, LayoutDashboard, GraduationCap, Shield, ChevronDown, Sparkles, Menu, X } from 'lucide-react';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Get dashboard path based on role
  const getDashboardPath = () => {
    switch (user?.role) {
      case 'admin': return '/admin';
      case 'instructor': return '/instructor';
      default: return '/dashboard';
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-xl text-slate-900">AISkillFlow</span>
          </Link>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center gap-8">
            <Link 
              to="/courses" 
              className="text-slate-600 hover:text-primary transition-colors font-medium"
              data-testid="nav-courses"
            >
              Courses
            </Link>
            <Link 
              to="/premium" 
              className="text-amber-600 hover:text-amber-500 transition-colors font-semibold flex items-center gap-1"
              data-testid="nav-premium"
            >
              Premium
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger className="text-slate-600 hover:text-primary transition-colors font-medium flex items-center gap-1" data-testid="nav-academy">
                AI Academy
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/academy" className="cursor-pointer">
                    Academy Home
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/academy/courses" className="cursor-pointer">
                    All Courses
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/academy/start-here" className="cursor-pointer">
                    Start Here
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/academy/dashboard" className="cursor-pointer">
                    My Dashboard
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/academy/membership" className="cursor-pointer">
                    Membership
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/academy/coach" className="cursor-pointer flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-primary" />
                    AI Coach
                  </Link>
                </DropdownMenuItem>
                {user?.role === 'admin' && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/academy/admin" className="cursor-pointer text-primary font-medium">
                        Admin Panel
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <Link 
              to="/blog" 
              className="text-slate-600 hover:text-primary transition-colors font-medium"
              data-testid="nav-blog"
            >
              Blog
            </Link>
            <Link 
              to="/pricing" 
              className="text-slate-600 hover:text-primary transition-colors font-medium"
              data-testid="nav-pricing"
            >
              Pricing
            </Link>
            <Link 
              to="/help" 
              className="text-slate-600 hover:text-primary transition-colors font-medium"
              data-testid="nav-help"
            >
              Help
            </Link>
          </div>

          {/* Auth Section */}
          <div className="flex items-center gap-4">
            {/* Mobile hamburger */}
            <button
              className="md:hidden p-2 text-slate-600 hover:text-primary"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      {user.role === 'admin' ? (
                        <Shield className="w-4 h-4 text-primary" />
                      ) : (
                        <User className="w-4 h-4 text-primary" />
                      )}
                    </div>
                    <span className="hidden sm:inline font-medium">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 ${
                      user.role === 'admin' ? 'bg-red-100 text-red-800' : 'bg-primary/10 text-primary'
                    }`}>
                      {user.role}
                    </span>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(getDashboardPath())} data-testid="nav-dashboard">
                    {user.role === 'admin' ? (
                      <Shield className="w-4 h-4 mr-2" />
                    ) : (
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                    )}
                    {user.role === 'admin' ? 'Admin Dashboard' : 'Dashboard'}
                  </DropdownMenuItem>
                  {user.role === 'student' && (
                    <DropdownMenuItem onClick={() => navigate('/courses')} data-testid="nav-my-courses">
                      <BookOpen className="w-4 h-4 mr-2" />
                      Browse Courses
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="nav-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')} data-testid="nav-login">
                  Log in
                </Button>
                <Button onClick={() => navigate('/register')} className="bg-primary hover:bg-primary/90" data-testid="nav-register">
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-slate-200 bg-white/95 backdrop-blur-sm" data-testid="mobile-menu">
          <div className="px-4 py-3 space-y-1">
            <Link to="/courses" className="block px-3 py-2.5 rounded-lg text-slate-700 hover:bg-slate-100 font-medium" onClick={() => setMobileOpen(false)}>
              Courses
            </Link>
            <Link to="/premium" className="block px-3 py-2.5 rounded-lg text-amber-600 hover:bg-amber-50 font-semibold" onClick={() => setMobileOpen(false)}>
              Premium
            </Link>
            <div className="px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wide">AI Academy</div>
            <Link to="/academy" className="block px-3 py-2.5 rounded-lg text-slate-700 hover:bg-slate-100 pl-6" onClick={() => setMobileOpen(false)}>
              Academy Home
            </Link>
            <Link to="/academy/courses" className="block px-3 py-2.5 rounded-lg text-slate-700 hover:bg-slate-100 pl-6" onClick={() => setMobileOpen(false)}>
              All Courses
            </Link>
            <Link to="/academy/coach" className="block px-3 py-2.5 rounded-lg text-slate-700 hover:bg-slate-100 pl-6 flex items-center gap-1.5" onClick={() => setMobileOpen(false)}>
              <Sparkles className="w-3.5 h-3.5 text-primary" /> AI Coach
            </Link>
            <Link to="/academy/membership" className="block px-3 py-2.5 rounded-lg text-slate-700 hover:bg-slate-100 pl-6" onClick={() => setMobileOpen(false)}>
              Membership
            </Link>
            <Link to="/blog" className="block px-3 py-2.5 rounded-lg text-slate-700 hover:bg-slate-100 font-medium" onClick={() => setMobileOpen(false)}>
              Blog
            </Link>
            <Link to="/pricing" className="block px-3 py-2.5 rounded-lg text-slate-700 hover:bg-slate-100 font-medium" onClick={() => setMobileOpen(false)}>
              Pricing
            </Link>
            <Link to="/help" className="block px-3 py-2.5 rounded-lg text-slate-700 hover:bg-slate-100 font-medium" onClick={() => setMobileOpen(false)}>
              Help
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
